import { useEffect, useState } from 'preact/hooks';
import { BUTTON_MAPPING_KEY, DEFAULT_BUTTON_MAPPING, type ButtonMapping } from '../engine/input/buttons';
import { ensurePersistentStorage, type StorageStatus } from '../store/persistence';
import { useApp } from './app-context';
import { BackupPanel } from './BackupPanel';
import { formatBytes } from './files';
import { LOW_LATENCY_KEY } from './tools';

export function Settings() {
  const { repo, device, storage: initial } = useApp();
  const [storage, setStorage] = useState<StorageStatus>(initial);
  const [rev, setRev] = useState<number | null>(null);
  const [lastBackup, setLastBackup] = useState<number | undefined>();
  const [mapping, setMapping] = useState<ButtonMapping>(DEFAULT_BUTTON_MAPPING);
  const [lowLatency, setLowLatency] = useState(true);

  useEffect(() => {
    void repo.currentRev().then(setRev);
    void repo.getSetting<number>('lastBackupAt').then(setLastBackup);
    void repo.getSetting<ButtonMapping>(BUTTON_MAPPING_KEY).then((m) => m && setMapping(m));
    void repo.getSetting<boolean>(LOW_LATENCY_KEY).then((v) => setLowLatency(v !== false));
  }, [repo]);

  return (
    <div class="screen">
      <header class="topbar">
        <a class="btn" href="#/">← Notebooks</a>
        <h1>Settings</h1>
      </header>
      <main class="content">
        <section class="card">
          <h2>Storage</h2>
          <dl class="kv">
            <dt>Persistent storage</dt>
            <dd>
              <span class={`pill ${storage.persist}`}>{storage.persist}</span>
              {storage.persist === 'denied' && (
                <button class="btn small" onClick={async () => setStorage(await ensurePersistentStorage(repo))}>Ask again</button>
              )}
            </dd>
            {storage.persist === 'denied' && (
              <dd class="warn-text">
                The browser may evict this app’s data under storage pressure. Installing the app to the home screen usually
                gets persistence granted. Until then, back up often.
              </dd>
            )}
            {storage.firstRequestedAt && <><dt>First requested</dt><dd>{new Date(storage.firstRequestedAt).toLocaleString()}</dd></>}
            {storage.usage !== undefined && <><dt>Used</dt><dd>{formatBytes(storage.usage)}{storage.quota ? ` of ${formatBytes(storage.quota)}` : ''}</dd></>}
            <dt>Local revision</dt><dd>{rev ?? '…'}</dd>
            <dt>Last backup</dt><dd>{lastBackup ? new Date(lastBackup).toLocaleString() : 'never'}</dd>
            <dt>Origin</dt><dd><code>{location.origin}</code></dd>
          </dl>
        </section>

        <section class="card">
          <h2>Backup</h2>
          <BackupPanel />
        </section>

        <section class="card">
          <h2>Ink</h2>
          <label class="check">
            <input
              type="checkbox" checked={lowLatency}
              onChange={(e) => {
                const v = (e.currentTarget as HTMLInputElement).checked;
                setLowLatency(v);
                void repo.setSetting(LOW_LATENCY_KEY, v);
              }}
            />
            Low-latency ink (desynchronized canvas)
          </label>
          <p class="hint">Turn off if ink flickers or the page renders wrongly while writing. Takes effect when you reopen a notebook.</p>
        </section>

        <section class="card">
          <h2>Pen buttons</h2>
          <p class="hint">
            While a mapped button is held, the pen acts as an eraser (stroke eraser arrives in Milestone 4; until then it
            only shows a ring and draws nothing). Open a notebook and turn on <b>Debug</b> to see which
            <code>button</code>/<code>buttons</code> values your pen sends.
          </p>
          <div class="checks">
            {([
              ['barrelErases', 'Side (barrel) button: buttons & 2'],
              ['eraserBitErases', 'Eraser button / eraser end: buttons & 32'],
              ['otherErases', 'Any other button: buttons & 4, 8 or 16'],
            ] as const).map(([k, label]) => (
              <label key={k} class="check">
                <input
                  type="checkbox" checked={mapping[k]}
                  onChange={(e) => {
                    const next = { ...mapping, [k]: (e.currentTarget as HTMLInputElement).checked };
                    setMapping(next);
                    void repo.setSetting(BUTTON_MAPPING_KEY, next);
                  }}
                />
                {label}
              </label>
            ))}
          </div>
        </section>

        <section class="card">
          <h2>This device</h2>
          <dl class="kv">
            <dt>Device record id</dt><dd><code>{device.id}</code></dd>
            <dt>Screen</dt><dd>{device.screen.width} × {device.screen.height} CSS px @ {device.devicePixelRatio}×</dd>
            <dt>Touch points</dt><dd>{device.maxTouchPoints}</dd>
            <dt>User agent</dt><dd><code class="wrap">{device.userAgent}</code></dd>
          </dl>
        </section>
      </main>
    </div>
  );
}
